import * as React from "react";
import { useQuery, useMutation, useQueryClient } from "react-query";
import { useHistory } from "react-router-dom";
import { ReportJob, ReportJobCollection, MetricsService, ReportJobStatus, ReportJobType } from "gen/clients";
import { DataTable } from "components/DataTable/DataTable";
import { DataTableRowAction } from "components/DataTable/types";
import { ApiErrorMessage } from "components/ApiErrorMessage/ApiErrorMessage";
import { TokenBasedPaginator } from "components/TokenBasedPaginator/TokenBasedPaginator";
import { ConfirmationDialog } from "components/ConfirmationDialog/ConfirmationDialog";
import { Chip, LinearProgress, Box, Typography } from "@mui/material";
import { formatDate } from "utils/dateUtils";

interface ClientContactsReportsListProps {
  onRunningReportsChange?: (hasRunningReports: boolean) => void;
}

const ClientContactsReportsList: React.FC<ClientContactsReportsListProps> = ({ onRunningReportsChange }) => {
  const history = useHistory();
  const queryClient = useQueryClient();
  const [rowsPerPage, setRowsPerPage] = React.useState<number>(10);
  const [nextPageToken, setNextPageToken] = React.useState<string>();
  const [pageToken, setPageToken] = React.useState<string>();
  const paginatorResetRef = React.useRef<() => void>(() => {
    window.console.log("placeholder function");
  });

  // Dialog states
  const [cancelDialogOpen, setCancelDialogOpen] = React.useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = React.useState(false);
  const [selectedReport, setSelectedReport] = React.useState<ReportJob | null>(null);

  const { data, isLoading, error } = useQuery<ReportJobCollection>(
    ["listClientContactsReports", pageToken, rowsPerPage],
    {
      queryFn: () =>
        MetricsService.listReportJobs({
          type: ReportJobType.CLIENT_CONTACTS,
          pageToken,
          pageSize: rowsPerPage
        }),
      onSuccess: collection => {
        setNextPageToken(collection.nextPageToken);
        // Check for running reports and notify parent
        const hasRunningReports = collection.items.some(
          report => report.status === ReportJobStatus.PENDING || report.status === ReportJobStatus.RUNNING
        );
        onRunningReportsChange?.(hasRunningReports);
      },
      refetchInterval: data => {
        // Refetch every 5 seconds if there are any running reports
        const hasRunningReports = data?.items.some(
          report => report.status === ReportJobStatus.PENDING || report.status === ReportJobStatus.RUNNING
        );
        return hasRunningReports ? 5000 : false;
      }
    }
  );

  // Cancel report mutation
  const {
    mutate: cancelReport,
    isLoading: isCancelling,
    error: cancelError
  } = useMutation(MetricsService.cancelReportJob, {
    onSuccess: () => {
      queryClient.invalidateQueries(["listClientContactsReports"]);
      setCancelDialogOpen(false);
      setSelectedReport(null);
    }
  });

  // Delete report mutation
  const {
    mutate: deleteReport,
    isLoading: isDeleting,
    error: deleteError
  } = useMutation(MetricsService.deleteReportJob, {
    onSuccess: () => {
      queryClient.invalidateQueries(["listClientContactsReports"]);
      setDeleteDialogOpen(false);
      setSelectedReport(null);
    }
  });

  const onRowsPerPageChange = React.useCallback((newRowsPerPage: number) => {
    setRowsPerPage(newRowsPerPage);
    setPageToken(undefined);
    setNextPageToken(undefined);
    paginatorResetRef?.current();
  }, []);

  const handleRowClick = React.useCallback(
    (report: ReportJob) => {
      if (report.status === ReportJobStatus.COMPLETED) {
        history.push(`/analytics/clientContactsReports/${report.id}`);
      }
    },
    [history]
  );

  const getStatusChip = React.useCallback((status: ReportJobStatus) => {
    switch (status) {
      case ReportJobStatus.PENDING:
        return <Chip label="Pending" color="default" size="small" />;
      case ReportJobStatus.RUNNING:
        return <Chip label="Running" color="info" size="small" />;
      case ReportJobStatus.COMPLETED:
        return <Chip label="Completed" color="success" size="small" />;
      case ReportJobStatus.FAILED:
        return <Chip label="Failed" color="error" size="small" />;
      case ReportJobStatus.CANCELLED:
        return <Chip label="Cancelled" color="warning" size="small" />;
      default:
        return <Chip label={status} color="default" size="small" />;
    }
  }, []);

  const getProgressCell = React.useCallback((report: ReportJob) => {
    if (report.status === ReportJobStatus.RUNNING && report.progress) {
      const { current = 0, total = 0, percentage = 0 } = report.progress;
      return (
        <Box sx={{ width: "100%" }}>
          <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
            <Box sx={{ width: "100%", mr: 1 }}>
              <LinearProgress variant="determinate" value={percentage} />
            </Box>
            <Box sx={{ minWidth: 35 }}>
              <Typography variant="body2" color="text.secondary">
                {Math.round(percentage)}%
              </Typography>
            </Box>
          </Box>
          <Typography variant="caption" color="text.secondary">
            {current} / {total} contacts processed
          </Typography>
        </Box>
      );
    }
    return null;
  }, []);

  // Dialog handlers
  const handleCancelClick = React.useCallback((report: ReportJob) => {
    setSelectedReport(report);
    setCancelDialogOpen(true);
  }, []);

  const handleDeleteClick = React.useCallback((report: ReportJob) => {
    setSelectedReport(report);
    setDeleteDialogOpen(true);
  }, []);

  const closeCancelDialog = React.useCallback(() => {
    setCancelDialogOpen(false);
    setSelectedReport(null);
  }, []);

  const closeDeleteDialog = React.useCallback(() => {
    setDeleteDialogOpen(false);
    setSelectedReport(null);
  }, []);

  const confirmCancel = React.useCallback(() => {
    if (selectedReport) {
      cancelReport({ reportId: selectedReport.id });
    }
  }, [cancelReport, selectedReport]);

  const confirmDelete = React.useCallback(() => {
    if (selectedReport) {
      deleteReport({ reportId: selectedReport.id });
    }
  }, [deleteReport, selectedReport]);

  // Row actions based on report status
  const getRowActions = React.useCallback(
    (report: ReportJob): DataTableRowAction[] => {
      const actions: DataTableRowAction[] = [];

      // Cancel action - only for running or pending reports
      if (report.status === ReportJobStatus.PENDING || report.status === ReportJobStatus.RUNNING) {
        actions.push({
          title: "Cancel",
          action: () => handleCancelClick(report),
          color: "warning"
        });
      }

      // Delete action - for all reports
      actions.push({
        title: "Delete",
        action: () => handleDeleteClick(report),
        color: "error"
      });

      return actions;
    },
    [handleCancelClick, handleDeleteClick]
  );

  return (
    <>
      <DataTable
        isLoading={isLoading}
        error={!!error && <ApiErrorMessage apiError={error} />}
        columns={[
          {
            id: "id",
            title: "Report ID",
            cell: (report: ReportJob) => report.id
          },
          {
            id: "status",
            title: "Status",
            cell: (report: ReportJob) => getStatusChip(report.status)
          },
          {
            id: "progress",
            title: "Progress",
            cell: getProgressCell
          },
          {
            id: "createdBy",
            title: "Created By",
            cell: (report: ReportJob) => report.createdBy
          },
          {
            id: "createdAt",
            title: "Created At",
            cell: (report: ReportJob) => formatDate(report.createdAt)
          },
          {
            id: "completedAt",
            title: "Completed At",
            cell: (report: ReportJob) => (report.completedAt ? formatDate(report.completedAt) : "-")
          }
        ]}
        data={data?.items || []}
        rowKey={(report: ReportJob) => report.id}
        onRowClick={handleRowClick}
        rowActions={getRowActions}
        dense={true}
      />
      <TokenBasedPaginator
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={onRowsPerPageChange}
        onPageTokenChange={setPageToken}
        nextPageToken={nextPageToken}
        currentPageDataRows={data?.items.length}
        rowsPerPageOptions={[10, 20, 50, 100]}
        resetRef={paginatorResetRef}
      />

      {/* Cancel confirmation dialog */}
      {cancelDialogOpen && selectedReport && (
        <ConfirmationDialog
          title="Cancel Report"
          content={
            <>
              Are you sure you want to cancel the report "{selectedReport.id}"? This action will stop the report
              generation process.
            </>
          }
          onClose={closeCancelDialog}
          onConfirm={confirmCancel}
          confirmationButtonColor="warning"
          isLoading={isCancelling}
          error={cancelError ? <ApiErrorMessage apiError={cancelError} /> : undefined}
        />
      )}

      {/* Delete confirmation dialog */}
      {deleteDialogOpen && selectedReport && (
        <ConfirmationDialog
          title="Delete Report"
          content={
            <>
              Are you sure you want to delete the report "{selectedReport.id}"? This action cannot be undone and will
              permanently remove the report and all associated files.
            </>
          }
          onClose={closeDeleteDialog}
          onConfirm={confirmDelete}
          confirmationButtonColor="error"
          isLoading={isDeleting}
          error={deleteError ? <ApiErrorMessage apiError={deleteError} /> : undefined}
        />
      )}
    </>
  );
};

export { ClientContactsReportsList };
